import { useStore } from "@nanostores/preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { ApiError, createMarker, deleteMarker, editMarker } from "../net/api";
import { $markerPanel, $markers, dropMarker, type MarkerPanelState, upsertMarker } from "../state/markers";
import { $panel, toast } from "../state/stores";
import { targetLabel } from "../world/markers";
import { describeError } from "./errors";
import { Panel } from "./Panel";

const MAX = 500;
const failText = (e: unknown) => (e instanceof ApiError ? describeError(e.code, e.message) : "無法連線到伺服器");
const when = (s: string) => new Date(s).toLocaleString("zh-TW", { dateStyle: "short", timeStyle: "short" });

function close(): void {
  $markerPanel.set(null);
  $panel.set(null);
}

/** Writes a new marker or shows one (spec 2h §2.3, §2.5). */
export function MarkerPanel() {
  const st = useStore($markerPanel);
  // leaving the panel by any route (Esc, another panel) drops the form and with it the preview pin
  useEffect(() => () => $markerPanel.set(null), []);
  if (!st) return null;
  return st.mode === "create" ? <CreateMarker st={st} /> : <ViewMarker st={st} />;
}

function CreateMarker({ st }: { st: Extract<MarkerPanelState, { mode: "create" }> }) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const busy = useRef(false); // a ref, so a second submit in the same tick sees the first (Review Focus 4)

  async function submit(e: Event) {
    e.preventDefault();
    if (busy.current) return;
    if (!text.trim()) { setError("請寫下要做的事"); return; }
    busy.current = true;
    setSending(true);
    setError(null);
    try {
      const m = await createMarker(st.plotId, { pos: st.pos, ...(st.target ? { target: { entity_id: st.target.entity_id } } : {}), body: text });
      upsertMarker(st.plotId, m); // the pin shows before MarkersChanged arrives
      close();
    } catch (err) {
      busy.current = false;
      setSending(false);
      if (err instanceof ApiError && err.status === 403) { toast("你已不是這塊領地的成員"); close(); return; }
      setError(failText(err));
    }
  }

  return (
    <Panel title="agent 標記">
      <form onSubmit={submit}>
        <p class="hint">{targetLabel(st.target ? { name: st.target.name } : null)} · {st.plotName}</p>
        <textarea class="marker-text" value={text} maxLength={MAX} rows={4} autoFocus placeholder="要 agent 做什麼？例如：這裡放一張長椅"
          onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
        <div class="marker-actions">
          <button type="submit" disabled={sending}>送出</button>
          <button type="button" class="secondary" onClick={close}>取消</button>
        </div>
        {error && <div class="error">{error}</div>}
      </form>
    </Panel>
  );
}

function ViewMarker({ st }: { st: Extract<MarkerPanelState, { mode: "view" }> }) {
  const all = useStore($markers);
  const m = all[st.plotId]?.find((x) => x.id === st.id);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (!m) { toast("標記已被刪除"); close(); } }, [m]);
  if (!m) return null;

  async function save(e: Event) {
    e.preventDefault();
    if (busy) return;
    if (!text.trim()) { setError("請寫下要做的事"); return; }
    setBusy(true);
    setError(null);
    try {
      upsertMarker(st.plotId, await editMarker(st.plotId, st.id, text));
      setEditing(false);
    } catch (err) {
      setError(failText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMarker(st.plotId, st.id);
    } catch (err) {
      if (!(err instanceof ApiError && err.code === "MARKER_NOT_FOUND")) { setBusy(false); setError(failText(err)); return; }
    }
    close(); // before dropping it, so the "deleted elsewhere" toast does not fire for our own delete
    dropMarker(st.plotId, st.id);
  }

  return (
    <Panel title="agent 標記" onClose={close}>
      <p class="hint">{targetLabel(m.target)}</p>
      {editing ? (
        <form onSubmit={save}>
          <textarea class="marker-text" value={text} maxLength={MAX} rows={4} autoFocus onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
          {m.status === "done" && <p class="hint">送出後會變回待處理</p>}
          <div class="marker-actions">
            <button type="submit" disabled={busy}>送出</button>
            <button type="button" class="secondary" onClick={() => { setEditing(false); setError(null); }}>取消</button>
          </div>
        </form>
      ) : <p class="marker-body">{m.body}</p>}
      <p class="hint">{m.created_by.display_name} · {when(m.created_at)}</p>
      {m.status === "done" && (
        <div class="marker-reply">
          <strong>已完成</strong>
          <p class="marker-body">{m.reply}</p>
          <p class="hint">{m.resolved_by?.display_name}{m.resolved_at ? ` · ${when(m.resolved_at)}` : ""}</p>
        </div>
      )}
      {!editing && (
        <div class="marker-actions">
          <button disabled={busy} onClick={() => { setText(m.body); setEditing(true); }}>編輯描述</button>
          <button class="secondary" disabled={busy} onClick={() => void remove()}>刪除</button>
        </div>
      )}
      {error && <div class="error">{error}</div>}
    </Panel>
  );
}
